import React from "react";
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Container,
  Chip,
  useTheme,
  Paper,
  LinearProgress,
  Button,
} from "@mui/material";
import {
  Package,
  IndianRupee,
  Users,
  ShoppingBag,
  BarChart3,
  PlusCircle,
  TrendingUp,
  TrendingDown,
  ArrowRight,
} from "lucide-react";
import { useNavigate } from "react-router-dom";

// Stat Cards Component with click functionality
const StatCard = ({ title, value, icon, color, trend, onClick }) => (
  <Card
    sx={{
      height: "100%",
      background: `linear-gradient(135deg, ${color}20, ${color}10)`,
      cursor: "pointer",
      transition: "all 0.3s ease",
      "&:hover": {
        transform: "translateY(-4px)",
        boxShadow: 3,
        background: `linear-gradient(135deg, ${color}30, ${color}20)`,
      },
    }}
    onClick={onClick}
  >
    <CardContent>
      <Box display="flex" alignItems="center" justifyContent="space-between">
        <Box>
          <Typography color="textSecondary" gutterBottom variant="overline">
            {title}
          </Typography>
          <Typography variant="h4" component="div" fontWeight="bold">
            {value}
          </Typography>
          {trend && (
            <Box display="flex" alignItems="center" mt={1}>
              {trend.includes("+") ? (
                <TrendingUp size={16} color="#4CAF50" />
              ) : (
                <TrendingDown size={16} color="#f44336" />
              )}
              <Chip
                label={trend}
                size="small"
                color={trend.includes("+") ? "success" : "error"}
                sx={{ ml: 1 }}
              />
            </Box>
          )}
        </Box>
        <Box
          sx={{
            p: 2,
            borderRadius: 2,
            backgroundColor: `${color}20`,
            color: color,
          }}
        >
          {icon}
        </Box>
      </Box>
    </CardContent>
  </Card>
);

// Simple Chart Component using MUI
const SimpleBarChart = ({ data, color }) => (
  <Box sx={{ display: "flex", alignItems: "end", height: 120, gap: 1 }}>
    {data.map((item, index) => (
      <Box
        key={index}
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          flex: 1,
        }}
      >
        <Typography variant="caption" color="textSecondary">
          {item.value}
        </Typography>
        <Box
          sx={{
            width: "100%",
            height: `${item.value * 3}px`,
            backgroundColor: color,
            borderRadius: 1,
            maxHeight: 80,
          }}
        />
        <Typography variant="caption" color="textSecondary" sx={{ mt: 1 }}>
          {item.name}
        </Typography>
      </Box>
    ))}
  </Box>
);

// Progress Chart Component
const ProgressChart = ({ title, value, color }) => (
  <Box sx={{ mb: 2 }}>
    <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
      <Typography variant="body2">{title}</Typography>
      <Typography variant="body2" fontWeight="bold">
        {value}%
      </Typography>
    </Box>
    <LinearProgress
      variant="determinate"
      value={value}
      sx={{
        height: 8,
        borderRadius: 4,
        backgroundColor: `${color}20`,
        "& .MuiLinearProgress-bar": {
          backgroundColor: color,
        },
      }}
    />
  </Box>
);

// Quick Action Card Component
const QuickActionCard = ({ title, icon, description, onClick, color }) => (
  <Card
    sx={{
      p: 3,
      textAlign: "center",
      cursor: "pointer",
      transition: "all 0.3s ease",
      background: `linear-gradient(135deg, ${color}10, ${color}05)`,
      border: `1px solid ${color}20`,
      "&:hover": {
        transform: "translateY(-4px)",
        boxShadow: 4,
        background: `linear-gradient(135deg, ${color}20, ${color}10)`,
      },
    }}
    onClick={onClick}
  >
    <Box sx={{ mb: 2, color: color }}>
      {React.cloneElement(icon, { size: 32 })}
    </Box>
    <Typography variant="h6" fontWeight="medium" gutterBottom>
      {title}
    </Typography>
    <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
      {description}
    </Typography>
    <Button
      endIcon={<ArrowRight size={16} />}
      sx={{ color: color, fontWeight: "medium" }}
    >
      View Details
    </Button>
  </Card>
);

const Dashboard = () => {
  const theme = useTheme();
  const navigate = useNavigate();

  // Sample data
  const salesData = [
    { name: "Jan", value: 40 },
    { name: "Feb", value: 30 },
    { name: "Mar", value: 20 },
    { name: "Apr", value: 27 },
    { name: "May", value: 18 },
    { name: "Jun", value: 23 },
  ];

  // Click handlers for different sections
  const handleViewPayments = () => {
    navigate(`/home/tools-payments`);
  };

  const handleViewOrders = () => {
    console.log("Navigate to orders page");
    // Replace with actual navigation: navigate('/orders')
    navigate(`/home/tools-orders`);
  };

  const handleAddProduct = () => {
    navigate(`/home/tools`);
  };

  const handleViewProducts = () => {
    console.log("Navigate to products page");
    // Replace with actual navigation: navigate('/products')
  };

  const handleViewCustomers = () => {
    console.log("Navigate to customers page");
    // Replace with actual navigation: navigate('/customers')
  };

  const handleViewAnalytics = () => {
    console.log("Navigate to analytics page");
    // Replace with actual navigation: navigate('/analytics')
  };

  // Quick actions data
  const quickActions = [
    {
      title: "Add Product",
      icon: <PlusCircle />,
      description: "Add new products to your inventory",
      onClick: handleAddProduct,
      color: "#4CAF50",
    },
    {
      title: "View Orders",
      icon: <ShoppingBag />,
      description: "Manage and track customer orders",
      onClick: handleViewOrders,
      color: "#2196F3",
    },
    {
      title: "View Payments",
      icon: <IndianRupee />,
      description: "Monitor payment status",
      onClick: handleViewPayments,
      color: "#FF9800",
    },
    {
      title: "Analytics",
      icon: <BarChart3 />,
      description: "Detailed analytics and insights",
      onClick: handleViewAnalytics,
      color: "#9C27B0",
    },
  ];

  return (
    <Box
      sx={{
        minHeight: "100vh",
        backgroundColor: theme.palette.background.default,
        py: 4,
      }}
    >
      <Container maxWidth="xl">
        {/* Quick Actions Section */}
        <Box sx={{ mb: 4 }}>
          <Typography
            variant="h5"
            fontWeight="bold"
            gutterBottom
            sx={{ mb: 3 }}
          >
            Quick Actions
          </Typography>
          <Grid container spacing={3}>
            {quickActions.map((action, index) => (
              <Grid item xs={12} sm={6} md={3} key={index}>
                <QuickActionCard {...action} />
              </Grid>
            ))}
          </Grid>
        </Box>

        {/* Quick Stats - Clickable Cards */}
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              title="Total Revenue"
              value="₹0"
              icon={<IndianRupee size={24} />}
              color="#4CAF50"
              trend="+12.5%"
              onClick={handleViewPayments}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              title="Total Orders"
              value="0"
              icon={<ShoppingBag size={24} />}
              color="#2196F3"
              trend="+8.2%"
              onClick={handleViewOrders}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              title="Products"
              value="356"
              icon={<Package size={24} />}
              color="#FF9800"
              trend="+5.1%"
              onClick={handleViewProducts}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              title="Customers"
              value="80"
              icon={<Users size={24} />}
              color="#9C27B0"
              trend="+15.3%"
              onClick={handleViewCustomers}
            />
          </Grid>
        </Grid>

        {/* Charts Section */}
        <Grid container spacing={3} sx={{ mb: 4 }}>
          {/* Sales Trend Chart */}
          <Grid item xs={12} md={8}>
            <Card sx={{ cursor: "pointer" }} onClick={handleViewAnalytics}>
              <CardContent>
                <Box
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                  mb={2}
                >
                  <Typography variant="h6">Sales Trend</Typography>
                  <Button endIcon={<ArrowRight size={16} />} color="primary">
                    View Details
                  </Button>
                </Box>
                <SimpleBarChart data={salesData} color="#6366F1" />
              </CardContent>
            </Card>
          </Grid>

          {/* Performance Metrics */}
          <Grid item xs={12} md={4}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Performance Metrics
                </Typography>
                <ProgressChart
                  title="Order Completion"
                  value={75}
                  color="#4CAF50"
                />
                <ProgressChart
                  title="Payment Success"
                  value={85}
                  color="#2196F3"
                />
                <ProgressChart
                  title="Customer Satisfaction"
                  value={92}
                  color="#FF9800"
                />
                <ProgressChart
                  title="Inventory Turnover"
                  value={68}
                  color="#9C27B0"
                />
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Recent Activity & Quick Stats */}
        <Grid container spacing={3} sx={{ mb: 4 }}>
          {/* Recent Activity */}
          <Grid item xs={12} md={6}>
            <Card sx={{ cursor: "pointer" }} onClick={handleViewOrders}>
              <CardContent>
                <Box
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                  mb={2}
                >
                  <Typography variant="h6">Recent Activity</Typography>
                  <Button endIcon={<ArrowRight size={16} />} color="primary">
                    View All
                  </Button>
                </Box>
                <Box sx={{ mt: 2 }}>
                  {[
                    {
                      text: "New order #1234 received",
                      time: "2 min ago",
                      type: "order",
                    },
                    {
                      text: "Payment of ₹234 completed",
                      time: "5 min ago",
                      type: "payment",
                    },
                    {
                      text: "New product added to inventory",
                      time: "10 min ago",
                      type: "product",
                    },
                    {
                      text: "Customer support ticket resolved",
                      time: "15 min ago",
                      type: "support",
                    },
                  ].map((activity, index) => (
                    <Box
                      key={index}
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        py: 1.5,
                        borderBottom: "1px solid #eee",
                        "&:last-child": { borderBottom: "none" },
                      }}
                    >
                      <Box>
                        <Typography variant="body2" fontWeight="medium">
                          {activity.text}
                        </Typography>
                        <Typography variant="caption" color="textSecondary">
                          {activity.time}
                        </Typography>
                      </Box>
                      <Chip
                        label={activity.type}
                        size="small"
                        color={
                          activity.type === "order"
                            ? "primary"
                            : activity.type === "payment"
                            ? "success"
                            : activity.type === "product"
                            ? "warning"
                            : "default"
                        }
                      />
                    </Box>
                  ))}
                </Box>
              </CardContent>
            </Card>
          </Grid>

          {/* Quick Stats Overview */}
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Quick Overview
                </Typography>
                <Grid container spacing={2} sx={{ mt: 1 }}>
                  <Grid item xs={6}>
                    <Paper
                      sx={{
                        p: 2,
                        textAlign: "center",
                        bgcolor: "#E3F2FD",
                        cursor: "pointer",
                        transition: "all 0.2s ease",
                        "&:hover": { bgcolor: "#BBDEFB" },
                      }}
                      onClick={handleViewOrders}
                    >
                      <Typography variant="h6" color="#1976D2">
                        24
                      </Typography>
                      <Typography variant="caption">Pending Orders</Typography>
                    </Paper>
                  </Grid>
                  <Grid item xs={6}>
                    <Paper
                      sx={{
                        p: 2,
                        textAlign: "center",
                        bgcolor: "#E8F5E8",
                        cursor: "pointer",
                        transition: "all 0.2s ease",
                        "&:hover": { bgcolor: "#C8E6C9" },
                      }}
                      onClick={handleViewOrders}
                    >
                      <Typography variant="h6" color="#2E7D32">
                        156
                      </Typography>
                      <Typography variant="caption">Completed Today</Typography>
                    </Paper>
                  </Grid>
                  <Grid item xs={6}>
                    <Paper
                      sx={{
                        p: 2,
                        textAlign: "center",
                        bgcolor: "#FFF3E0",
                        cursor: "pointer",
                        transition: "all 0.2s ease",
                        "&:hover": { bgcolor: "#FFE0B2" },
                      }}
                      onClick={handleViewProducts}
                    >
                      <Typography variant="h6" color="#EF6C00">
                        12
                      </Typography>
                      <Typography variant="caption">Low Stock Items</Typography>
                    </Paper>
                  </Grid>
                  <Grid item xs={6}>
                    <Paper
                      sx={{
                        p: 2,
                        textAlign: "center",
                        bgcolor: "#FCE4EC",
                        cursor: "pointer",
                        transition: "all 0.2s ease",
                        "&:hover": { bgcolor: "#F8BBD0" },
                      }}
                      onClick={handleViewCustomers}
                    >
                      <Typography variant="h6" color="#C2185B">
                        8
                      </Typography>
                      <Typography variant="caption">New Customers</Typography>
                    </Paper>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
};

export default Dashboard;
